import type {
  StorefrontBannerItem,
  StorefrontContentBlock,
  StorefrontContentMedia,
  StorefrontProductSummary,
} from '@nucleus-commerce/api-client/admin';

import {
  bannerItemIsEmpty,
  toPreviewCta,
  toPreviewMediaRef,
  type PreviewBannerItem,
  type PreviewBlockBody,
  type PreviewMediaRef,
} from '../../api/mappers';
import type {
  BannerFormInput,
  MediaRefForm,
  RailFormInput,
  RailHeaderForm,
} from '../../schemas/block-form-schema';

/**
 * The panel's rendering model (plan 6.4): copy always comes from the form so it is live on every
 * keystroke; media, button hrefs and rail products come from the last server payload, but only
 * where the body that produced it still matches the form at that position. Anything else is
 * shown as pending until the next payload lands.
 */

export type PreviewMediaView =
  | { state: 'none' }
  | { state: 'pending' }
  /** The ref was sent but the store has no such media. */
  | { state: 'missing' }
  | { state: 'ready'; url: string; kind: 'image' | 'video'; alt: string };

export interface PreviewCtaView {
  label: string;
  href: string | null;
  /** `broken`: the product or collection is gone from this store. */
  state: 'ready' | 'pending' | 'broken';
}

export interface PreviewSlideView {
  eyebrow: string;
  title: string;
  subtitle: string;
  description: string;
  media: PreviewMediaView;
  mobileMedia: PreviewMediaView;
  ctas: PreviewCtaView[];
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Descriptions are shown as text: there is no HTML sanitiser in the admin yet (plan 3.7). */
export function htmlToText(html: string): string {
  if (html.trim() === '') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent.replace(/\s+/g, ' ').trim();
}

function fromPayload(media: StorefrontContentMedia): PreviewMediaView {
  return {
    state: 'ready',
    url: media.kind === 'image' ? (media.renditions?.large ?? media.url) : media.url,
    kind: media.kind,
    alt: media.alt,
  };
}

function mergeMedia(
  form: MediaRefForm | undefined,
  sent: PreviewMediaRef | null | undefined,
  resolved: StorefrontContentMedia | null | undefined,
  hasPayload: boolean,
): PreviewMediaView {
  const ref = toPreviewMediaRef(form);
  if (!ref) return { state: 'none' };
  if (hasPayload && same(sent ?? null, ref)) {
    return resolved ? fromPayload(resolved) : { state: 'missing' };
  }
  // A URL needs no lookup to be shown; a library pick waits for the server.
  if (ref.type === 'url')
    return { state: 'ready', url: ref.url, kind: ref.kind, alt: ref.alt ?? '' };
  return { state: 'pending' };
}

/** One slide (or a rail header): form copy over the payload's media and buttons. */
export function mergeSlide(
  form: RailHeaderForm,
  sent: PreviewBannerItem | null | undefined,
  resolved: StorefrontBannerItem | null | undefined,
): PreviewSlideView {
  const hasPayload = !!sent && !!resolved;
  let position = 0;
  const ctas = form.ctas.flatMap((cta): PreviewCtaView[] => {
    const label = cta.label.trim();
    const body = toPreviewCta(cta);
    if (!body) return label ? [{ label, href: null, state: 'pending' }] : [];
    const index = position++;
    const payload = resolved?.ctas[index];
    if (hasPayload && payload && same(sent.ctas?.[index]?.link, body.link)) {
      return [{ label, href: payload.href, state: payload.href ? 'ready' : 'broken' }];
    }
    return body.link.type === 'url'
      ? [{ label, href: body.link.url, state: 'ready' }]
      : [{ label, href: null, state: 'pending' }];
  });
  return {
    eyebrow: form.eyebrow.trim(),
    title: form.title.trim(),
    subtitle: form.subtitle.trim(),
    description: htmlToText(form.descriptionHtml),
    media: mergeMedia(form.media, sent?.media, resolved?.media, hasPayload),
    mobileMedia: mergeMedia(form.mobileMedia, sent?.mobileMedia, resolved?.mobileMedia, hasPayload),
    ctas,
  };
}

export function bannerSlides(
  values: BannerFormInput,
  sent: PreviewBlockBody | undefined,
  block: StorefrontContentBlock | undefined,
): PreviewSlideView[] {
  const sentItems = sent?.kind === 'banner' ? sent.banner.items : [];
  const resolvedItems = block?.banner?.items ?? [];
  return values.items.map((item, i) => mergeSlide(item, sentItems[i], resolvedItems[i]));
}

export type RailEmptyReason = 'pickProducts' | 'pickCollection' | 'nonePublished' | null;

export interface RailView {
  header: PreviewSlideView | null;
  /** Resolved cards: publication, market prices and `limit` already applied by the server. */
  products: StorefrontProductSummary[];
  /** Hand-picked products in the payload's body; null for a collection. */
  selected: number | null;
  /** Hand-picked products this store has not published, when the payload makes that certain. */
  unpublished: number | null;
  empty: RailEmptyReason;
  /** No payload yet for what the form holds. */
  loading: boolean;
}

export function railView(
  values: RailFormInput,
  sent: PreviewBlockBody | undefined,
  block: StorefrontContentBlock | undefined,
): RailView {
  const sentRail = sent?.kind === 'rail' ? sent.rail : undefined;
  const rail = block?.rail ?? undefined;
  const header = bannerItemIsEmpty(values.header)
    ? null
    : mergeSlide(values.header, sentRail?.header, rail?.header);
  const products = rail?.products ?? [];
  const selected = sentRail?.source.type === 'products' ? sentRail.source.productIds.length : null;
  // Under the limit nothing was cut, so every selected product missing from the payload is unpublished.
  const unpublished =
    selected !== null && rail && products.length < rail.limit ? selected - products.length : null;
  const noSource =
    values.sourceType === 'products' ? values.products.length === 0 : values.collectionId === '';
  const empty: RailEmptyReason = noSource
    ? values.sourceType === 'products'
      ? 'pickProducts'
      : 'pickCollection'
    : rail && products.length === 0
      ? 'nonePublished'
      : null;
  return { header, products, selected, unpublished, empty, loading: !noSource && !rail };
}

/** Things the block author can fix: media the store cannot find, buttons with a gone target, an empty rail. */
export function countIssues(view: PreviewSlideView[] | RailView): number {
  const slideIssues = (slide: PreviewSlideView) =>
    (slide.media.state === 'missing' ? 1 : 0) +
    (slide.mobileMedia.state === 'missing' ? 1 : 0) +
    slide.ctas.filter((cta) => cta.state === 'broken').length;
  if (Array.isArray(view)) return view.reduce((sum, slide) => sum + slideIssues(slide), 0);
  return (view.header ? slideIssues(view.header) : 0) + (view.empty === 'nonePublished' ? 1 : 0);
}
